import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { Crosshair } from 'lucide-react';
import { createPortal } from 'react-dom';

import { HighlightOverlay } from './HighlightOverlay';
import { useElementInspector } from './useElementInspector';

/**
 * Active mode indicator shown when the inspector is running
 */
function ActiveIndicator() {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const hotkeyHint = isMac ? '⌘⇧Z to exit' : 'Ctrl+Shift+Z to exit';

    return (
        <div
            data-element-inspector="indicator"
            className="fixed bottom-4 left-4 z-[10003] flex items-center gap-2 rounded-lg bg-accent-500 px-3 py-2 font-medium text-sm text-white shadow-lg"
        >
            <Crosshair className="h-4 w-4" />
            <span>Element Inspector Active</span>
            <span className="ml-2 rounded bg-white/20 px-1.5 py-0.5 text-xs">{hotkeyHint}</span>
        </div>
    );
}

/**
 * Dev mode warning banner shown when Sentry attributes may not be available
 */
function DevModeWarning() {
    const isDev = import.meta.env.DEV;

    if (!isDev) {
        return null;
    }

    return (
        <div
            data-element-inspector="warning"
            className="fixed right-4 bottom-4 z-[10003] max-w-xs rounded-lg border border-warning-border bg-warning-subtle px-3 py-2 text-warning-foreground text-xs shadow-lg"
        >
            <strong>Dev Mode:</strong> data-sentry-* attributes are only available in production builds.
        </div>
    );
}

/**
 * Element Inspector component that provides a visual tool for inspecting
 * React components and their Sentry attributes.
 *
 * Activated via Alt+Shift+I hotkey when enabled via feature flag.
 * Clicking an element copies its HTML with Sentry context to clipboard.
 *
 * @example
 * ```tsx
 * // In your root component:
 * <ElementInspector />
 * ```
 */
export function ElementInspector() {
    const isEnabled = useFeatureFlag('enable-element-inspector', false);
    const { state, hoveredElement } = useElementInspector();

    // Don't render anything if disabled or inactive
    if (!isEnabled || state === 'inactive') {
        return null;
    }

    // Render overlay via portal to document body
    return createPortal(
        <div data-element-inspector="root">
            <ActiveIndicator />
            <DevModeWarning />
            {hoveredElement && <HighlightOverlay inspectedElement={hoveredElement} />}
        </div>,
        document.body,
    );
}
